import { useSyncExternalStore, useCallback } from 'react'
import { siteConfig } from '@/config/site'
import type { Theme } from '@hun-tech/shared/types'

const THEME_CHANGE_EVENT = 'hun-tech-theme-change'

function getThemeSnapshot(): Theme {
  if (typeof window === 'undefined') return 'dark'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

function getServerSnapshot(): Theme {
  return 'dark'
}

function subscribe(callback: () => void) {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(THEME_CHANGE_EVENT, callback)
  window.addEventListener('storage', callback)
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, callback)
    window.removeEventListener('storage', callback)
  }
}

/**
 * Theme hook — reads from and writes to the DOM + localStorage.
 * Uses useSyncExternalStore with 'hun-tech-theme-change' custom event
 * to guarantee that multiple useTheme() calls across components remain synchronized.
 */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getThemeSnapshot, getServerSnapshot)

  const set = useCallback((next: Theme) => {
    if (typeof window === 'undefined') return
    document.documentElement.classList.toggle('dark', next === 'dark')
    try {
      localStorage.setItem(siteConfig.themeStorageKey, next)
    } catch {
      // localStorage may be unavailable in restricted environments
    }
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: next }))
  }, [])

  const toggle = useCallback(() => {
    const current =
      typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
        ? 'dark'
        : 'light'
    const next: Theme = current === 'dark' ? 'light' : 'dark'
    set(next)
  }, [set])

  return { theme, toggle, set, isDark: theme === 'dark' } as const
}
